import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const LABELS = { zoom: "Zoom", microsoft: "Outlook" };

const ERROR_COPY = {
  consent_denied: "You cancelled the connection.",
  invalid_state: "That connection link expired — start again from HQ.",
  missing_code: "The provider didn't return an authorization code.",
  token_exchange_failed: "The provider rejected the connection. Check the app credentials and redirect URI.",
  no_refresh_token: "The provider didn't grant offline access, so HQ can't keep the connection alive.",
  server_error: "Something went wrong finishing the connection.",
};

/**
 * Landing page for the OAuth popup used by the booking dialog.
 *
 * The dialog opens the provider's consent screen in a small window so the
 * half-typed meeting isn't lost. When the provider sends the browser back here,
 * we tell the opener what happened and close ourselves. If someone reaches this
 * page in a normal tab (popup blocked, bookmark, provider re-entry), it degrades
 * into a plain confirmation with a link back.
 */
export default function OAuthComplete() {
  const [searchParams] = useSearchParams();
  const [standalone, setStandalone] = useState(false);

  const connected = searchParams.get("connected");
  const error = searchParams.get("error");
  const provider = searchParams.get("provider") || connected || "";
  const label = LABELS[provider] || "Your account";
  const ok = Boolean(connected) && !error;

  useEffect(() => {
    const opener = window.opener;
    if (opener && !opener.closed) {
      try {
        opener.postMessage(
          { type: "hq-meeting-oauth", provider, ok, error: error || null },
          window.location.origin
        );
      } catch {
        /* opener on another origin — it just won't get the nudge */
      }
      const timer = setTimeout(() => window.close(), 400);
      // If the browser refuses to close us, show the standalone message instead.
      const fallback = setTimeout(() => setStandalone(true), 1500);
      return () => {
        clearTimeout(timer);
        clearTimeout(fallback);
      };
    }
    setStandalone(true);
    return undefined;
  }, [provider, ok, error]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8 bg-canvas"
      data-testid="oauth-complete"
    >
      <div className="max-w-sm text-center">
        {ok ? (
          <CheckCircle2 className="w-9 h-9 mx-auto text-emerald-700" strokeWidth={1.5} />
        ) : (
          <AlertTriangle className="w-9 h-9 mx-auto text-red-700" strokeWidth={1.5} />
        )}
        <h1 className="heading-2 text-foreground mt-4">
          {ok ? `${label} connected` : `Couldn't connect ${label}`}
        </h1>
        <p className="body-text mt-2">
          {ok
            ? "You can close this window and carry on booking."
            : ERROR_COPY[error] || "Please try again from HQ."}
        </p>
        {standalone && (
          <Link
            to="/meetings"
            className="inline-block mt-6 text-sm text-foreground underline underline-offset-4"
          >
            Back to Meetings
          </Link>
        )}
      </div>
    </div>
  );
}
